import React, { useState } from "react";
import { householdApi, sharingApi } from "../services/api";
import "./HouseholdSettings.css";

export default function HouseholdSettings({ household, onClose, onLeave }) {
  const [chatIds, setChatIds] = useState(household.telegram_chat_ids || "");
  const [username, setUsername] = useState(household.username || "");
  const [usernameCheck, setUsernameCheck] = useState(null);
  const [checkingUsername, setCheckingUsername] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingUsername, setSavingUsername] = useState(false);
  const [saved, setSaved] = useState(false);
  const [usernameSaved, setUsernameSaved] = useState(false);
  const [error, setError] = useState(null);

  const handleUsernameChange = async (val) => {
    setUsername(val);
    setUsernameCheck(null);
    if (val.trim().length >= 3) {
      setCheckingUsername(true);
      try {
        const result = await sharingApi.checkUsername(val.trim());
        setUsernameCheck(result);
      } catch {}
      finally { setCheckingUsername(false); }
    }
  };

  const handleSaveUsername = async () => {
    if (!username.trim()) return;
    setSavingUsername(true);
    setError(null);
    try {
      await sharingApi.setUsername(username.trim());
      localStorage.setItem("household_username", username.trim());
      setUsernameSaved(true);
      setTimeout(() => setUsernameSaved(false), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Username already taken.");
    } finally {
      setSavingUsername(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const ids = chatIds.split(",").map((s) => s.trim()).filter(Boolean);
      await householdApi.update(household.id, { telegram_chat_ids: ids });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch {
      setError("Failed to save settings.");
    } finally {
      setSaving(false);
    }
  };

  const handleLeave = () => {
    if (!window.confirm("Leave this household? You'll need the invite code to rejoin.")) return;
    localStorage.removeItem("household_id");
    localStorage.removeItem("household_name");
    localStorage.removeItem("household_code");
    localStorage.removeItem("household_username");
    onLeave();
  };

  const isUsernameAvailable = usernameCheck?.available === false ||
    username === household.username;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="settings-modal" onClick={(e) => e.stopPropagation()}>
        <div className="settings-header">
          <h2 className="settings-title">⚙️ Household Settings</h2>
          <button className="settings-close" onClick={onClose}>✕</button>
        </div>

        {error && <div className="settings-error">{error}</div>}

        <div className="settings-section">
          <label className="settings-label">Household Name</label>
          <div className="settings-value">{household.name}</div>
        </div>

        <div className="settings-section">
          <label className="settings-label">Invite Code</label>
          <div className="invite-code-display">
            <span className="invite-code">{household.invite_code}</span>
            <button className="btn-copy"
              onClick={() => navigator.clipboard.writeText(household.invite_code)}>
              📋 Copy
            </button>
          </div>
          <p className="settings-hint">Share this code with family members to join</p>
        </div>

        <div className="settings-section">
          <label className="settings-label">@Username <span style={{fontWeight:400,textTransform:"none",fontSize:"10px"}}>(for recipe sharing)</span></label>
          <div className="settings-username-wrap">
            <span className="settings-at">@</span>
            <input
              type="text"
              value={username}
              onChange={(e) => handleUsernameChange(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
              placeholder="e.g. rainbow or smith_family"
              className="settings-username-input"
              maxLength={20}
            />
            <button
              className="settings-username-save"
              onClick={handleSaveUsername}
              disabled={savingUsername || !username.trim() || username === household.username}
            >
              {savingUsername ? "…" : usernameSaved ? "✅" : "Set"}
            </button>
          </div>
          {checkingUsername && <p className="settings-hint">Checking availability…</p>}
          {usernameCheck && !checkingUsername && username !== household.username && (
            <p className="settings-hint" style={{color: usernameCheck.available ? "#ef4444" : "#22c55e"}}>
              {usernameCheck.available ? `❌ @${username} is taken` : `✅ @${username} is available`}
            </p>
          )}
          {household.username && (
            <p className="settings-hint">Current: @{household.username}</p>
          )}
        </div>

        <div className="settings-section">
          <label className="settings-label">Telegram Chat IDs</label>
          <input
            type="text"
            value={chatIds}
            onChange={(e) => setChatIds(e.target.value)}
            placeholder="123456789, 987654321"
            className="settings-input"
          />
          <p className="settings-hint">Separate multiple IDs with commas</p>
        </div>

        <div className="settings-actions">
          <button className="btn-leave" onClick={handleLeave}>Leave Household</button>
          <button className="btn-save" onClick={handleSave} disabled={saving}>
            {saving ? "Saving…" : saved ? "✅ Saved!" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
